import { useCallback, useEffect, useState } from 'react';
import { useLathat, useMuvelet } from '../felhasznalo-context.ts';

import { api, type BontasRendezes, type NapiBontasValasz } from '../api.ts';
import { useAlapadatok } from '../alapadatok-context.ts';
import { bontasParameterek, letolt } from '../letoltes.ts';
import { ft, szam } from '../formazas.ts';

const RENDEZESEK: BontasRendezes[] = ['forgalom', 'ertek', 'etterem', 'utolso', 'cegcsoport'];

/**
 * Napi bontás — a hónap forgalma étterem × nap mátrixban. (Megbízás 6.4)
 *
 * **Minden partner szerepel, a nem vásárlók is.** Ez a képernyő lényege: ebből
 * derül ki, ki maradt el. A nem vásárló sorok üresek, de ott vannak.
 *
 * Csak a **lezárt** napok adatai látszanak — amíg egy nap ellenőrzés alatt van,
 * a számai még változhatnak.
 */
export function NapiBontas({ onKarton }: { onKarton: (etterem_id: number) => void }) {
  const alapadatok = useAlapadatok();

  /** Letöltés csak engedéllyel. (3.0 / 1.) */
  const letolthet = useMuvelet('export_kimutatas');
  /** Számlaadat csak annak, aki a Számlák képernyőt is látja. */
  const szamlatLat = useLathat('szamla');
  const [honapok, setHonapok] = useState<string[]>([]);
  const [honap, setHonap] = useState<string | null>(null);
  const [kereses, setKereses] = useState('');
  const [keresesKesleltetve, setKeresesKesleltetve] = useState('');
  const [fizetesiMod, setFizetesiMod] = useState('');
  const [cegcsoportra, setCegcsoportra] = useState(false);
  const [nemVasarlokIs, setNemVasarlokIs] = useState(true);
  const [rendezes, setRendezes] = useState<BontasRendezes>('forgalom');
  const [bontas, setBontas] = useState<NapiBontasValasz | null>(null);
  const [hiba, setHiba] = useState<string | null>(null);
  const [toltes, setToltes] = useState(true);
  const [nemVasarlokNyitva, setNemVasarlokNyitva] = useState(false);

  useEffect(() => {
    api
      .get<{ honapok: string[] }>('/api/kimutatas/honapok')
      .then((v) => {
        setHonapok(v.honapok);
        setHonap((h) => h ?? v.honapok[0] ?? null);
      })
      .catch((h: Error) => setHiba(h.message));
  }, []);

  useEffect(() => {
    const i = setTimeout(() => setKeresesKesleltetve(kereses), 250);
    return () => clearTimeout(i);
  }, [kereses]);

  const betolt = useCallback(async () => {
    if (!honap) return;
    setToltes(true);
    try {
      const p = new URLSearchParams();
      if (keresesKesleltetve.trim()) p.set('q', keresesKesleltetve.trim());
      if (fizetesiMod) p.set('fizetesi_mod', fizetesiMod);
      if (cegcsoportra) p.set('cegcsoportra', '1');
      if (!nemVasarlokIs) p.set('nem_vasarlok_is', '0');
      p.set('rendezes', rendezes);
      setBontas(await api.get<NapiBontasValasz>(`/api/kimutatas/bontas/${honap}?${p}`));
      setHiba(null);
    } catch (h) {
      setHiba(h instanceof Error ? h.message : 'Nem sikerült betölteni a kimutatást.');
    } finally {
      setToltes(false);
    }
  }, [honap, keresesKesleltetve, fizetesiMod, cegcsoportra, nemVasarlokIs, rendezes]);

  useEffect(() => {
    void betolt();
  }, [betolt]);

  const evek = [...new Set(honapok.map((h) => h.slice(0, 4)))];
  const napok = bontas?.napok ?? [];
  const lezartak = new Set(bontas?.lezart_napok ?? []);

  /**
   * A tábla alján a napok összesítve. (A megrendelő kérése, 2026-09-18)
   *
   * A látszó sorokból számoljuk, tehát a szűrést és a cégcsoportra vonást is
   * követi: ami a képernyőn van, annak az összege áll alul.
   */
  const latszoSorok = bontas?.sorok ?? [];
  const napiOsszeg: Record<string, number> = {};
  for (const s of latszoSorok) {
    for (const [d, l] of Object.entries(s.napok)) napiOsszeg[d] = (napiOsszeg[d] ?? 0) + l;
  }
  const osszLiter = latszoSorok.reduce((a, s) => a + s.liter, 0);
  const osszErtek = latszoSorok.reduce((a, s) => a + s.ertek, 0);

  return (
    <>
      <div className="dayhead">
        {/* Év választó: a hónaplista évek múlva is rövid marad. (2026-09-17) */}
        {evek.length > 1 && (
          <select
            value={honap?.slice(0, 4) ?? ''}
            title="Év"
            onChange={(e) => {
              const elso = honapok.find((h) => h.startsWith(e.target.value));
              if (elso) setHonap(elso);
            }}
          >
            {evek.map((ev) => (
              <option key={ev} value={ev}>
                {ev}
              </option>
            ))}
          </select>
        )}
        <select value={honap ?? ''} onChange={(e) => setHonap(e.target.value)}>
          {honapok
            .filter((h) => !honap || evek.length <= 1 || h.startsWith(honap.slice(0, 4)))
            .map((h) => (
              <option key={h} value={h}>
                {honapFelirat(h)}
              </option>
            ))}
        </select>

        <input
          className="qi"
          style={{ width: 250, fontSize: 12.5 }}
          placeholder="szűrés étteremre vagy csoportra…"
          value={kereses}
          onChange={(e) => setKereses(e.target.value)}
        />

        <label className="sw">
          <input
            type="checkbox"
            checked={cegcsoportra}
            onChange={(e) => setCegcsoportra(e.target.checked)}
          />
          cégcsoportra összevonva
        </label>

        <label className="sw">
          <input
            type="checkbox"
            checked={nemVasarlokIs}
            onChange={(e) => setNemVasarlokIs(e.target.checked)}
          />
          nem vásárlók is
        </label>

        <select
          value={fizetesiMod}
          onChange={(e) => setFizetesiMod(e.target.value)}
          style={{
            font: 'inherit',
            fontSize: 12.5,
            padding: '5px 8px',
            border: '1px solid var(--rule2)',
            background: '#fff',
          }}
        >
          <option value="">Minden fizetési mód</option>
          {alapadatok.fizetesi_modok.map((m) => (
            <option key={m.kod} value={m.kod}>
              {m.nev}
            </option>
          ))}
        </select>

        {letolthet && (
          <button
            className="btn sm"
            onClick={() =>
              letolt(
                `/api/export/bontas/${honap}${bontasParameterek({ q: keresesKesleltetve, fizetesiMod, cegcsoportra, nemVasarlokIs, rendezes })}`,
              )
            }
          >
            Excel
          </button>
        )}

        {/*
          * Havi elszámolás: minden számla mellett a kiszállított mennyiség,
          * az iroda saját táblájának formájában, plusz a heti bontás. A
          * keresőbe írt szövegre szól — például egy cégcsoport nevére.
          * (2026-10-05, a megrendelő kérése)
          */}
        {letolthet && szamlatLat && (
          <button
            className="btn sm"
            title="Minden számla a hozzá tartozó literrel, plusz heti bontás — a jelenlegi keresésre"
            onClick={() =>
              letolt(
                `/api/export/havi-elszamolas/${honap}` +
                  (keresesKesleltetve ? `?q=${encodeURIComponent(keresesKesleltetve)}` : ''),
              )
            }
          >
            Elszámolás (számla + liter)
          </button>
        )}
      </div>

      <div className="sortbar" style={{ marginTop: 14 }}>
        <span>Rendezés</span>
        {RENDEZESEK.map((r) => (
          <button
            key={r}
            className={`sb${rendezes === r ? ' on' : ''}`}
            onClick={() => setRendezes(r)}
          >
            {bontas?.rendezes_nevek[r] ?? r}
          </button>
        ))}
        <span className="hint">
          {(bontas?.lezart_napok.length ?? 0) === 0 && (bontas?.elozmeny_napok.length ?? 0) === 0
            ? 'Ebben a hónapban még nincs lezárt nap — a kimutatás csak lezárt napokból dolgozik.'
            : `${bontas?.lezart_napok.length ?? 0} lezárt nap` +
              ((bontas?.elozmeny_napok.length ?? 0) > 0
                ? `, ${bontas!.elozmeny_napok.length} nap forgalmi előzményből (halványan)`
                : '') +
              ' ebben a hónapban.'}
        </span>
      </div>

      {hiba && <div className="uzenet bad">{hiba}</div>}

      <div className="cards">
        {(bontas?.kartyak ?? []).map((k, i) => {
          const nemVasarloKartya = k.cimke === 'Nem vásárolt partner';
          return (
            <div
              key={k.cimke}
              className={`card${nemVasarloKartya ? ' warn2' : ''}`}
              onClick={nemVasarloKartya ? () => setNemVasarlokNyitva((v) => !v) : undefined}
            >
              <em>{k.cimke}</em>
              <b>{k.ertek}</b>
              {k.megjegyzes && <span>{k.megjegyzes}</span>}
              {i === 3 && nemVasarlokNyitva && <span> ▲</span>}
            </div>
          );
        })}
      </div>

      {nemVasarlokNyitva && bontas && (
        <div className="misslist">
          <p className="lbl">
            Nem vásárolt partnerek — {bontas.nem_vasarlok.length} db
            {letolthet && (
              <button
                className="btn sm"
                style={{ marginLeft: 10 }}
                onClick={() =>
                  letolt(
                    `/api/export/nem-vasarlok/${honap}${bontasParameterek({ q: keresesKesleltetve, fizetesiMod, cegcsoportra, nemVasarlokIs, rendezes })}`,
                  )
                }
              >
                Excel
              </button>
            )}
          </p>
          <table className="tt2">
            <thead>
              <tr>
                <th>Étterem</th>
                <th>Cím</th>
                <th>Típus</th>
                <th>Utolsó vásárlás</th>
              </tr>
            </thead>
            <tbody>
              {bontas.nem_vasarlok.map((n) => (
                <tr key={n.etterem_id}>
                  <td className="nm3" onClick={() => onKarton(n.etterem_id)}>
                    <b>{n.nev}</b>
                  </td>
                  <td style={{ padding: '6px 10px', color: 'var(--muted)' }}>{n.cim ?? '—'}</td>
                  <td style={{ padding: '6px 10px' }}>
                    {n.tipus === 'csak_gyujtes' ? (
                      <span className="only">csak gyűjtés</span>
                    ) : (
                      'olajvásárló'
                    )}
                  </td>
                  <td
                    className="calc2"
                    style={{ textAlign: 'left' }}
                  >
                    {n.utolso_vasarlas ? (
                      <span className={(n.hanyadik_honapja ?? 0) > 2 ? 'bad2' : undefined}>
                        {n.utolso_vasarlas}
                        {n.hanyadik_honapja !== null && ` · ${n.hanyadik_honapja} hónapja`}
                      </span>
                    ) : (
                      <span className="halvany">még sosem</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* --- a mátrix --- */}
      <div className="matrix" style={{ maxHeight: 640, overflow: 'auto' }}>
        <table className="tt2">
          <thead>
            <tr>
              <th style={{ minWidth: 220 }}>Étterem</th>
              {napok.map((d) => (
                <th
                  key={d}
                  className="n"
                  title={lezartak.has(d) ? `${d} — lezárva` : `${d} — még nincs lezárva`}
                  style={{ width: 34, color: lezartak.has(d) ? undefined : 'var(--ghost)' }}
                >
                  {d.slice(8)}
                </th>
              ))}
              <th className="n">Liter</th>
              <th className="n">Érték</th>
              <th className="n">Utoljára</th>
            </tr>
          </thead>
          <tbody>
            {(bontas?.sorok ?? []).map((s) => (
              <tr key={`${s.etterem_id ?? s.nev}`} className={s.vasarolt ? undefined : 'nul'}>
                <td
                  className={`pn${s.etterem_id ? ' kclick' : ''}`}
                  onClick={s.etterem_id ? () => onKarton(s.etterem_id!) : undefined}
                >
                  {s.nev}
                  {s.cim && <small>{s.cim}</small>}
                </td>
                {napok.map((d) => {
                  const l = s.napok[d];
                  // A forgalmi előzményből jövő adat halványan látszik:
                  // abban csak liter van, ár és elszámolás nincs. (2.0 / 6.3)
                  const becsult = s.becsult_napok.includes(d);
                  return (
                    <td
                      key={d}
                      className={`v${l ? '' : ' z'}${becsult ? ' becsult' : ''}`}
                      title={becsult ? 'Forgalmi előzmény — becsült adat, ár nélkül' : undefined}
                    >
                      {l ? szam(l, 0) : '·'}
                    </td>
                  );
                })}
                <td className="tt">{s.liter ? szam(s.liter, 0) : '—'}</td>
                <td className="tt">{s.ertek ? ft(s.ertek) : '—'}</td>
                <td className="tt">
                  {s.hanyadik_honapja === null ? (
                    <span className="halvany">soha</span>
                  ) : (
                    <span className={s.hanyadik_honapja > 2 ? 'bad2' : undefined}>
                      {s.hanyadik_honapja === 0 ? 'e havi' : `${s.hanyadik_honapja} hó`}
                    </span>
                  )}
                </td>
              </tr>
            ))}
            {(bontas?.sorok.length ?? 0) === 0 && (
              <tr>
                <td
                  colSpan={napok.length + 4}
                  style={{ padding: 26, textAlign: 'center', color: '#A3A9B0' }}
                >
                  {toltes ? 'Betöltés…' : 'Nincs találat.'}
                </td>
              </tr>
            )}
          </tbody>
          {latszoSorok.length > 0 && (
            <tfoot>
              <tr>
                <td className="pn">Napi összesen</td>
                {napok.map((d) => (
                  <td key={d} className={`v${napiOsszeg[d] ? '' : ' z'}`} title={d}>
                    {napiOsszeg[d] ? szam(napiOsszeg[d], 0) : '·'}
                  </td>
                ))}
                <td className="tt">{osszLiter ? szam(osszLiter, 0) : '—'}</td>
                <td className="tt">{osszErtek ? ft(osszErtek) : '—'}</td>
                <td className="tt" />
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </>
  );
}

function honapFelirat(honap: string): string {
  const [ev, ho] = honap.split('-');
  const nevek = [
    'január',
    'február',
    'március',
    'április',
    'május',
    'június',
    'július',
    'augusztus',
    'szeptember',
    'október',
    'november',
    'december',
  ];
  return `${ev}. ${nevek[Number(ho) - 1] ?? ho}`;
}
